import { useCallback } from 'react'

import type { ApolloCache } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { forgetPostScheduledTimeChange } from '~publish/helpers/posts/recentPostScheduledTimeChanges'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'
import { updatePostScheduledTimeCache } from '~publish/pages/Calendar/hooks/updatePostScheduledTimeCache'

/**
 * GraphQL mutation for dropping a post to a new date.
 */
export const DropPost = graphql(/* GraphQL */ `
  mutation DropPost($input: DropPostInput!) {
    dropPost(input: $input) {
      __typename
      ... on PostActionSuccess {
        post {
          __typename
          id
          dueAt
          isCustomScheduled
          isPinned
        }
      }
      ... on MutationError {
        message
      }
    }
  }
`)

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export const useDropPostMutation = () => {
  const [dropPost, result] = useTypedMutation(
    DropPost,
    (data) => data.dropPost,
    {
      successTypename: 'PostActionSuccess',
      optimisticResponse: buildOptimisticResponse,
      update: (cache: ApolloCache, { data }, { variables }) => {
        if (data?.dropPost.__typename === 'PostActionSuccess') {
          updatePostScheduledTimeCache(cache, {
            id: data.dropPost.post.id,
            dueAt: data.dropPost.post.dueAt ?? variables?.input.dueAt ?? null,
            isCustomScheduled: data.dropPost.post.isCustomScheduled,
            isPinned: data.dropPost.post.isPinned,
          })
        }
      },
    },
  )

  const dropPostWithGuardCleanup = useCallback(
    async (...options: Parameters<typeof dropPost>) => {
      const dropResult = await dropPost(...options)
      const postId = options[0]?.variables?.input.id

      if (!dropResult.success && postId) {
        // Apollo removes the optimistic cache layer on failure. Clear the
        // matching realtime guard as well so a legitimate server-side update
        // is not mistaken for a stale echo after the rollback.
        forgetPostScheduledTimeChange(postId)
      }

      return dropResult
    },
    [dropPost],
  )

  return [dropPostWithGuardCleanup, result] as const
}

function buildOptimisticResponse({
  input,
}: {
  input: VariablesOf<typeof DropPost>['input']
}): ResultOf<typeof DropPost> {
  return {
    dropPost: {
      __typename: 'PostActionSuccess',
      post: {
        __typename: 'Post',
        id: input.id,
        dueAt: input.dueAt,
        isCustomScheduled: false,
        isPinned: input.isPinned ?? false,
      },
    },
  }
}
